import React from "react";
import { Link, useLocation } from "react-router-dom";
import classNames from "classnames";
import {
  HomeIcon,
  BriefcaseIcon,
  BanknotesIcon,
  UsersIcon,
  ArchiveBoxIcon,
  WrenchScrewdriverIcon,
  TruckIcon,
  Cog8ToothIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import {
  HELP_SIDEBAR_GROUP_ORDER,
  helpCoverageRegistry,
} from "@/features/help-hub/coverage/coverage.registry";
import { getFeatureHelpPath } from "@/features/help-hub/help-links";
import { getFeatureIcon } from "@/features/help-hub/feature-icons";

const SIDEBAR_GROUP_ICONS = {
  Overview: <HomeIcon className="h-4 w-4" />,
  Operations: <BriefcaseIcon className="h-4 w-4" />,
  Finance: <BanknotesIcon className="h-4 w-4" />,
  Workforce: <UsersIcon className="h-4 w-4" />,
  Assets: <ArchiveBoxIcon className="h-4 w-4" />,
  Productivity: <WrenchScrewdriverIcon className="h-4 w-4" />,
  Settings: <Cog8ToothIcon className="h-4 w-4" />,
  "Account & Support": <QuestionMarkCircleIcon className="h-4 w-4" />,
};

const DocSidebar = ({ items, activeSectionId }) => {
  const location = useLocation();

  const renderLink = ({ path, title, helpSectionId }) => {
    const hasHash = path.includes("#");
    const isActive = hasHash
      ? activeSectionId === helpSectionId
      : location.pathname === path;

    return (
      <Link
        to={path}
        className={classNames(
          "text-sm font-medium hover:text-primary-600 block rounded px-1.5 py-0.5 text-left break-words leading-tight",
          isActive ? "text-primary-700 bg-primary-50" : "text-gray-700"
        )}
      >
        {title}
      </Link>
    );
  };

  const renderNavItem = (item) => {
    return (
      <div key={item.path} className="mb-3">
        <div className="mb-2 flex items-center text-left gap-2">
          {item.icon && <span className="text-gray-500">{item.icon}</span>}
          {renderLink(item)}
        </div>
        {item.items && (
          <div className="pl-4 border-l border-gray-200">
            {item.items.map((subItem) => (
              <div key={subItem.path} className="block py-1 text-sm">
                <Link
                  to={subItem.path}
                  className={classNames(
                    "block rounded px-1.5 py-0.5",
                    activeSectionId === subItem.helpSectionId
                      ? "text-primary-700 bg-primary-50"
                      : "text-gray-600 hover:text-primary-600"
                  )}
                >
                  {subItem.title}
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <nav className="w-full pl-3 pr-2">
      <div className="pb-8">
        {items.map((section) => (
          <div key={section.title} className="mb-8">
            <h5 className="mb-3 text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center">
              {section.icon && <span className="mr-2">{section.icon}</span>}
              {section.title}
            </h5>
            <div className="space-y-1">{section.items.map(renderNavItem)}</div>
          </div>
        ))}
      </div>
    </nav>
  );
};

const DocumentationLayout = ({ children, sections = [], activeSectionId }) => {
  const effectiveSections = sections.length ? sections : helpCoverageRegistry;

  const groupedSections = effectiveSections.reduce((acc, section) => {
    const group = section.sidebarGroup || "Operations";
    if (!acc[group]) acc[group] = [];
    acc[group].push(section);
    return acc;
  }, {});

  const orderedGroups = HELP_SIDEBAR_GROUP_ORDER.filter(
    (groupTitle) => groupedSections[groupTitle]?.length
  );

  const sidebarItems = orderedGroups.map((groupTitle) => ({
    title: groupTitle,
    icon: SIDEBAR_GROUP_ICONS[groupTitle] || <TruckIcon className="h-4 w-4" />,
    items: groupedSections[groupTitle].map((section) => ({
      title: section.title,
      path: getFeatureHelpPath(section),
      helpSectionId: section.helpSectionId,
      icon: getFeatureIcon(section.id, "h-4 w-4"),
      items: [],
    })),
  }));

  return (
    <div className="h-full bg-white">
      <div className="w-full h-full">
        <div className="py-4 h-full">
          <div className="flex h-full min-h-0">
            <aside className="w-72 pr-4 h-full shrink-0">
              <div className="sticky top-0 h-full overflow-y-auto">
                <DocSidebar items={sidebarItems} activeSectionId={activeSectionId} />
              </div>
            </aside>
            <div className="flex-1 min-w-0 pl-4 h-full overflow-y-auto">
              <div className="prose prose-lg max-w-none pb-8">{children}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentationLayout;
